import { useInvalidateWorkflowExecution } from '@commenda/api/workflow-execution/useInvalidateWorkflowExecution';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { TRedirectStepDefinition } from '@commenda/types';
import {
  EnterpriseSwitchAccountUrlHelper,
  isSafeRedirectUrl,
} from '@commenda/utils';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { useEffect, useMemo } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { isCurrentTimelineTask, updateTaskStatus } from '../../../utils';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';

export const WorkflowRedirectTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, mutate, workflowExecution, serviceRequest } = context;
  const isCurrentTask = isCurrentTimelineTask(task);
  const { setNotification } = useNotification();
  const invalidateWorkflowExecution = useInvalidateWorkflowExecution();

  const markAsCompleteModalVisibility = useToggle();
  const isMarkingAsComplete = useToggle(false);

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as TRedirectStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const resolvedUrl = useMemo(() => {
    if (!stepDefinition) return '';
    const stepExecution = Object.values(
      workflowExecution?.runtime?.stepExecutions ?? {}
    ).find((e) => e.stepDefinitionId === task.workflowDefinitionStepId);
    return (
      String(stepExecution?.outputData?.resolvedRedirectUrl ?? '') ||
      (stepDefinition.valueMapping ? '' : (stepDefinition.redirectUrl ?? ''))
    );
  }, [stepDefinition, workflowExecution, task.workflowDefinitionStepId]);

  const goToPageUrl = useMemo(() => {
    const companyId =
      serviceRequest.companyId ?? serviceRequest.corporation?.companyId;
    if (role !== TaskParty.ADMIN || !companyId) return resolvedUrl;
    if (!isSafeRedirectUrl(resolvedUrl)) return resolvedUrl;
    return EnterpriseSwitchAccountUrlHelper.getUrl({
      companyId,
      redirectTo: resolvedUrl,
    });
  }, [
    role,
    resolvedUrl,
    serviceRequest.companyId,
    serviceRequest.corporation?.companyId,
  ]);

  useEffect(() => {
    const taskIsActive =
      task.status !== TaskStatus.COMPLETED &&
      task.status !== TaskStatus.BACKLOG;

    if (!stepDefinition?.valueMapping || resolvedUrl || !taskIsActive) return;
    if (!workflowExecution?.id) return;

    const interval = setInterval(() => {
      invalidateWorkflowExecution(workflowExecution.id);
    }, 1000);

    return () => clearInterval(interval);
  }, [
    resolvedUrl,
    stepDefinition?.valueMapping,
    task.status,
    workflowExecution?.id,
  ]);

  const markAsComplete = async () => {
    isMarkingAsComplete.on();
    const result = await updateTaskStatus({
      task,
      mutate,
      status: TaskStatus.COMPLETED,
    });

    if (!result) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to mark as complete',
      });
      markAsCompleteModalVisibility.hide();
      isMarkingAsComplete.off();
      return;
    }

    if (workflowExecution?.id) {
      invalidateWorkflowExecution(workflowExecution.id);
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Task marked as complete',
    });
    markAsCompleteModalVisibility.hide();
    isMarkingAsComplete.off();
  };

  if (!workflowExecution || !stepDefinition) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Failed to load workflow step information
        </p>
      </div>
    );
  }

  const goToPageButton = goToPageUrl ? (
    <Button
      size="sm"
      variant="primary"
      onClick={() => window.open(goToPageUrl, '_blank', 'noopener,noreferrer')}
    >
      <Icons.ArrowUpRight className="w-4" />
      Go to page
    </Button>
  ) : null;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <div className="flex gap-2">
                  {goToPageButton}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={markAsCompleteModalVisibility.show}
                  >
                    <Icons.Check className="w-4" />
                    Mark as completed
                  </Button>
                </div>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <div className="flex gap-2">
                  {goToPageButton}
                  <Button
                    size="sm"
                    onClick={markAsCompleteModalVisibility.show}
                  >
                    <Icons.Check className="w-4" />
                    Mark as completed
                  </Button>
                </div>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" variant="secondary" disabled>
                Mark as completed
              </Button>
            ),
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                {goToPageButton}
                <Button
                  size="sm"
                  onClick={markAsCompleteModalVisibility.show}
                  variant="secondary"
                >
                  <Icons.Check className="w-4" />
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Mark as completed on client's behalf"
                    : 'Mark as completed'}
                </Button>
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                {goToPageButton}
                <Button
                  size="sm"
                  onClick={markAsCompleteModalVisibility.show}
                  variant="secondary"
                >
                  <Icons.Check className="w-4" />
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Mark as completed on client's behalf"
                    : 'Mark as completed'}
                </Button>
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.ADMIN]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                {goToPageButton}
                <Button
                  size="sm"
                  onClick={markAsCompleteModalVisibility.show}
                  variant="secondary"
                >
                  <Icons.Check className="w-4" />
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Mark as completed on client's behalf"
                    : 'Mark as completed'}
                </Button>
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                {goToPageButton}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={markAsCompleteModalVisibility.show}
                >
                  <Icons.Check className="w-4" />
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Mark as completed on client's behalf"
                    : 'Mark as completed'}
                </Button>
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={`${task.title} completed`}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={markAsCompleteModalVisibility.visible}
        onClose={markAsCompleteModalVisibility.hide}
        onConfirm={markAsComplete}
        title="Mark as done"
        message="Please note that this action is irreversible. To revert this change, please contact Commenda support."
        confirmButtonText="Continue"
        hideCancelButton
        isActionLoading={isMarkingAsComplete.value}
      />
    </>
  );
};
